import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import LegalPageLayout from '../components/LegalPageLayout';

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSection {
  title: string;
  items: FAQItem[];
}

function AccordionItem({ question, answer }: FAQItem) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-white/10 last:border-b-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between py-4 text-left group"
      >
        <span className="text-white font-medium pr-4 group-hover:text-waffle-orange transition-colors">
          {question}
        </span>
        <span className={`text-gray-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
      </button>
      {isOpen && (
        <div className="pb-4 text-gray-300 text-sm leading-relaxed"
          dangerouslySetInnerHTML={{ __html: answer }}
        />
      )}
    </div>
  );
}

// Section keys and their question counts for iteration
const SECTION_KEYS = [
  { key: 'gettingStarted', qCount: 4 },
  { key: 'sharingPrivacy', qCount: 6 },
  { key: 'connections', qCount: 5 },
  { key: 'postsComments', qCount: 6 },
  { key: 'subscriptions', qCount: 5 },
  { key: 'accountManagement', qCount: 5 },
  { key: 'migration', qCount: 2 },
  { key: 'troubleshooting', qCount: 5 },
] as const;

export default function HelpCenterPage() {
  const { t } = useTranslation('help');

  const faqSections: FAQSection[] = SECTION_KEYS.map(({ key, qCount }) => ({
    title: t(`sections.${key}.title`),
    items: Array.from({ length: qCount }, (_, i) => ({
      question: t(`sections.${key}.q${i + 1}.question`),
      answer: t(`sections.${key}.q${i + 1}.answer`),
    })),
  }));

  return (
    <LegalPageLayout title={t('title')}>
      <p className="text-lg text-gray-300 mb-8">
        {t('pageIntro')}{' '}
        <a href="mailto:support@familywaffle.com" className="text-waffle-orange hover:underline">
          support@familywaffle.com
        </a>.
      </p>

      {faqSections.map((section) => (
        <div key={section.title} className="mb-10">
          <h2 className="text-xl font-semibold text-white mb-4 border-b border-white/10 pb-2">
            {section.title}
          </h2>
          <div>
            {section.items.map((item) => (
              <AccordionItem key={item.question} {...item} />
            ))}
          </div>
        </div>
      ))}

      <div className="mt-12 rounded-xl bg-gradient-to-r from-waffle-orange/10 to-amber-500/10 border border-waffle-orange/20 p-6 text-center">
        <h3 className="text-lg font-semibold text-white mb-2">{t('stillNeedHelp')}</h3>
        <p className="text-gray-300 mb-4">
          {t('supportTeamHere')}
        </p>
        <a
          href="mailto:support@familywaffle.com"
          className="inline-block px-6 py-3 bg-gradient-to-r from-waffle-orange to-amber-500 text-white font-semibold rounded-lg hover:shadow-lg hover:shadow-waffle-orange/25 transition-all"
        >
          {t('contactSupport')}
        </a>
      </div>
    </LegalPageLayout>
  );
}
